import React, { useState } from 'react';
import { toast } from 'sonner';
import { IHSANROLL_OFFICERS } from '../../data/ihsanRollMock';
import {
  addMovement,
  formatTime,
  isOverdue,
  newMovementId,
  nowHHMM,
  openOutFor
} from '../../lib/ihsanRollStore';
import MorphButton from '../../components/MorphButton';
import { fieldClass } from './shared';

export default function MovementView({ store, caseNo, update, onDone }) {
  const officer = store.dutyOfficer;
  const open = openOutFor(store, caseNo);
  const [mode, setMode] = useState(open ? 'in' : 'out');
  const [time, setTime] = useState(nowHHMM);
  const [place, setPlace] = useState(open?.place || '');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [authorisedBy, setAuthorisedBy] = useState(officer || IHSANROLL_OFFICERS[0]);

  const submit = () => {
    if (!officer) {
      toast.error('Select a duty officer first', { position: 'top-center' });
      return false;
    }
    if (!time) {
      toast.error('Enter a time', { position: 'top-center' });
      return false;
    }
    if (mode === 'out') {
      if (open) {
        toast.error('This case is already out. Log a return first.', { position: 'top-center' });
        return false;
      }
      if (!place.trim() || !expectedReturn) {
        toast.error('Destination and expected return are required', { position: 'top-center' });
        return false;
      }
      update((prev) =>
        addMovement(prev, {
          id: newMovementId(),
          caseNo,
          type: 'out',
          time,
          place: place.trim(),
          expectedReturn,
          authorisedBy,
          officer,
          createdAt: new Date().toISOString()
        })
      );
      return true;
    }
    update((prev) =>
      addMovement(prev, {
        id: newMovementId(),
        caseNo,
        type: 'in',
        time,
        place: place.trim() || open?.place || '',
        officer,
        createdAt: new Date().toISOString(),
        closes: open?.id || null
      })
    );
    return true;
  };

  return (
    <div className="px-4 pt-5 pb-8" data-testid="ihsanroll-movement">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Case #{caseNo} · {officer || 'no officer'}
      </p>
      <h1 className="text-2xl font-extrabold text-[#0F172A] mt-1">Movement</h1>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {[
          { id: 'out', label: 'Going out' },
          { id: 'in', label: 'Reaching back' }
        ].map((tab) => {
          const active = mode === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              data-testid={`ihsanroll-movement-${tab.id}`}
              aria-pressed={active}
              onClick={() => {
                setMode(tab.id);
                if (tab.id === 'in' && open?.place) setPlace(open.place);
              }}
              className={`h-11 rounded-xl text-sm font-semibold ${
                active ? 'bg-primary text-white' : 'bg-muted/60 text-[#0F172A]'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {mode === 'in' && open && (
        <div
          className={`mt-4 rounded-xl border p-3 text-sm ${
            isOverdue(open) ? 'border-[#D64545]/40 bg-red-50 text-[#D64545]' : 'border-border bg-white'
          }`}
        >
          Out to {open.place} since {formatTime(open.time)}, expected {formatTime(open.expectedReturn)}.
        </div>
      )}
      {mode === 'in' && !open && (
        <p className="mt-4 text-sm text-muted-foreground">
          No open out for this case. You can still log a standalone return.
        </p>
      )}

      <div className="mt-5 flex flex-col gap-4">
        <div>
          <label className="text-xs font-semibold text-[#0F172A]" htmlFor="mv-time">Time</label>
          <input id="mv-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={fieldClass} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#0F172A]" htmlFor="mv-place">
            {mode === 'out' ? 'Going where' : 'Returning from'}
          </label>
          <input
            id="mv-place"
            type="text"
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            placeholder="Destination"
            className={fieldClass}
          />
        </div>
        {mode === 'out' && (
          <>
            <div>
              <label className="text-xs font-semibold text-[#0F172A]" htmlFor="mv-return">Expected return</label>
              <input
                id="mv-return"
                type="time"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F172A]" htmlFor="mv-auth">Authorised by</label>
              <select
                id="mv-auth"
                value={authorisedBy}
                onChange={(e) => setAuthorisedBy(e.target.value)}
                className={fieldClass}
              >
                {IHSANROLL_OFFICERS.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </>
        )}
        <p className="text-xs text-muted-foreground">Logged by {officer || '—'}</p>
      </div>

      <div className="mt-5">
        <MorphButton
          data-testid="ihsanroll-save-movement"
          idleLabel={mode === 'out' ? 'Save going out' : 'Save return'}
          successLabel="Movement saved"
          onClick={submit}
          onComplete={onDone}
          className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover"
        />
      </div>
    </div>
  );
}
